import { useState, useEffect, useRef } from 'react'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveField, type EditableTable } from '@/lib/editApi'
import { sanitizeHtml } from '@/lib/sanitize'
import { Loader2, Check, X } from 'lucide-react'

interface EditableHtmlProps {
  table: EditableTable
  id: string
  field: string
  value: string | null
  className?: string
  renderHtml: (html: string) => React.ReactNode
  onSaved?: (newValue: string) => void
}

export default function EditableHtml({
  table,
  id,
  field,
  value,
  className = '',
  renderHtml,
  onSaved
}: EditableHtmlProps) {
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()
  const containerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localValue, setLocalValue] = useState(value || '')

  useEffect(() => {
    setLocalValue(value || '')
    setHasChanges(false)
  }, [value])

  // Cuando se prende el modo edición, sincronizamos el innerHTML al
  // valor canónico. Cuando se apaga, también — descartamos cambios
  // pendientes no guardados para evitar dejar el DOM desincronizado.
  useEffect(() => {
    if (!innerRef.current) return
    innerRef.current.innerHTML = sanitizeHtml(localValue)
    setHasChanges(false)
  }, [isEditMode, localValue])

  // Detectar cambios para mostrar/ocultar la barra Guardar/Cancelar.
  const handleInput = () => {
    if (!innerRef.current) return
    const current = innerRef.current.innerHTML
    setHasChanges(current !== sanitizeHtml(localValue))
  }

  // Pegar como texto plano (evita HTML basura desde Word/Docs).
  const handlePaste = (e: React.ClipboardEvent) => {
    if (!isEditMode) return
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  // Esc cancela, Ctrl/Cmd+Enter guarda. Enter normal hace newline.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isEditMode) return
    if (e.key === 'Escape') {
      e.preventDefault()
      cancelChanges()
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      saveChanges()
    }
  }

  const cancelChanges = () => {
    if (!innerRef.current) return
    innerRef.current.innerHTML = sanitizeHtml(localValue)
    setHasChanges(false)
    setError(null)
  }

  const saveChanges = async () => {
    if (!innerRef.current || !profile?.id) return
    const newHtml = sanitizeHtml(innerRef.current.innerHTML)
    if (newHtml === localValue) {
      setHasChanges(false)
      return
    }

    setSaving(true)
    setError(null)
    const { error: err } = await saveField({
      table, id, field,
      oldValue: localValue,
      newValue: newHtml,
      editedBy: profile.id
    })
    setSaving(false)
    if (err) {
      setError(err.message)
      return
    }
    setLocalValue(newHtml)
    setHasChanges(false)
    onSaved?.(newHtml)
  }

  // Modo lectura puro: render normal sin nada extra
  if (!isEditMode) {
    return <>{renderHtml(localValue)}</>
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        ref={innerRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        className={`${className} editable-html-container focus:outline-none`}
      />

      {/* Barra flotante: aparece cuando hay cambios sin guardar */}
      {hasChanges && (
        <div className="fixed bottom-24 right-6 z-50 flex items-center gap-2 bg-white rounded-xl shadow-2xl border-2 border-amber-300 p-2">
          {saving ? (
            <span className="px-3 py-1.5 inline-flex items-center gap-2 text-amber-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Guardando...
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={saveChanges}
                title="Guardar (Ctrl+Enter)"
                className="px-3 py-1.5 inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white rounded-md text-sm font-medium"
              >
                <Check className="w-4 h-4" />
                Guardar cambios
              </button>
              <button
                type="button"
                onClick={cancelChanges}
                title="Descartar (Esc)"
                className="px-3 py-1.5 inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium"
              >
                <X className="w-4 h-4" />
                Descartar
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}

      {!hasChanges && !saving && (
        <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
          Hacé click en cualquier texto y editalo. Aparecerá un botón Guardar abajo a la derecha.
        </p>
      )}
    </div>
  )
}
